import { useEffect, useState } from 'react';

import { isNonProduction } from '@/utils/environmentByHostname';

import { isOccasionId, type OccasionId, occasionOn } from './occasions';

const PREVIEW_PARAM = 'occasion';
const PREVIEW_KEY = 'optoscale-occasion-preview';

/**
 * Outside production, `?occasion=christmas` shows an occasion on any day, for UAT and design
 * reviews; `?occasion=none` shows the plain fish and `?occasion=` drops the preview. It is kept
 * for the tab, since navigating drops the query string. Undefined means no preview.
 */
function readPreview(): OccasionId | null | undefined {
    if (!isNonProduction()) return undefined;
    try {
        const param = new URLSearchParams(window.location.search).get(PREVIEW_PARAM);
        if (param === '') sessionStorage.removeItem(PREVIEW_KEY);
        else if (param !== null) sessionStorage.setItem(PREVIEW_KEY, param);
        const stored = sessionStorage.getItem(PREVIEW_KEY);
        if (stored === 'none') return null;
        return isOccasionId(stored) ? stored : undefined;
    } catch {
        // Blocked storage: no preview, the calendar decides.
        return undefined;
    }
}

function msUntilNextDay(now: Date): number {
    const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    return next.getTime() - now.getTime();
}

/** The local calendar day, as a value that stays equal within the day. */
function localDay(now: Date): string {
    return `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
}

/**
 * Today's occasion in the viewer's time zone. It changes over at midnight while the tab stays
 * open, and is checked again whenever the tab comes back into view, since a sleeping laptop
 * delays the midnight timer and a new time zone moves midnight.
 */
export function useOccasion(): OccasionId | null {
    const [day, setDay] = useState(() => localDay(new Date()));
    const [preview] = useState(readPreview);

    useEffect(() => {
        const refresh = () => setDay(localDay(new Date()));
        const onVisibility = () => {
            if (document.visibilityState === 'visible') refresh();
        };
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('focus', refresh);
        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('focus', refresh);
        };
    }, []);

    useEffect(() => {
        // Scheduled from now rather than from the stored day, and a second past midnight so a
        // timer that fires a little early still lands on the new day.
        const timer = setTimeout(
            () => setDay(localDay(new Date())),
            msUntilNextDay(new Date()) + 1000
        );
        return () => clearTimeout(timer);
    }, [day]);

    if (preview !== undefined) return preview;
    const [year, month, date] = day.split('-').map(Number);
    return occasionOn(new Date(year, month - 1, date, 12));
}
